import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { SupportContactRow, SupportContactDraft } from "./types";

/**
 * Add / edit one support number.
 *
 * The two fiddly bits are both conversions the operator should never see:
 *
 *  - Times are stored as MINUTES FROM MIDNIGHT IST but edited in a native
 *    <input type="time">, which speaks "HH:MM".
 *  - "Available 24x7" is not a stored flag. It is start_minute and end_minute
 *    both being NULL, so the toggle clears the two boxes rather than setting
 *    anything — one representation of "all day" in the database, not two.
 */

const DAYS = [
  { value: 0, short: "Sun" },
  { value: 1, short: "Mon" },
  { value: 2, short: "Tue" },
  { value: 3, short: "Wed" },
  { value: 4, short: "Thu" },
  { value: 5, short: "Fri" },
  { value: 6, short: "Sat" },
];

export const minutesToTimeInput = (minute: number | null): string => {
  if (minute == null) return "";
  // 1440 is a legal end value (midnight tonight) that "24:00" cannot express in
  // an <input type="time">; show it as 23:59 and convert back on save.
  const m = minute >= 1440 ? 1439 : minute;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

export const timeInputToMinutes = (value: string): number | null => {
  if (!value) return null;
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// Mirrors the backend's normalisePhone (services/supportContact.ts): 6-15
// digits, with spaces / + / - / brackets stripped. Checked here too so a typo is
// caught before a round-trip, not instead of one.
const normalisePhone = (raw: string) =>
  raw.trim().replace(/[\s()\-.]/g, "").replace(/^\+/, "");

export const isValidPhone = (raw: string) => /^[0-9]{6,15}$/.test(normalisePhone(raw));

interface Props {
  open: boolean;
  // null = adding a new number.
  contact: SupportContactRow | null;
  saving: boolean;
  onClose: () => void;
  onSave: (draft: SupportContactDraft) => void;
}

const SupportContactDialog: React.FC<Props> = ({ open, contact, saving, onClose, onSave }) => {
  const [label, setLabel] = useState("");
  const [phone, setPhone] = useState("");
  const [priority, setPriority] = useState("100");
  const [isActive, setIsActive] = useState(true);
  const [days, setDays] = useState<number[]>([]);
  const [allDay, setAllDay] = useState(true);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  // Reseed every time the dialog opens so a cancelled edit leaves nothing behind.
  useEffect(() => {
    if (!open) return;
    setLabel(contact?.label ?? "");
    setPhone(contact?.phone ?? "");
    setPriority(String(contact?.priority ?? 100));
    setIsActive(contact?.isActive ?? true);
    setDays(contact?.daysOfWeek ?? []);
    const hasWindow = contact?.startMinute != null && contact?.endMinute != null;
    setAllDay(!hasWindow);
    setStart(minutesToTimeInput(contact?.startMinute ?? null));
    setEnd(minutesToTimeInput(contact?.endMinute ?? null));
  }, [open, contact]);

  const phoneInvalid = phone.length > 0 && !isValidPhone(phone);
  const windowIncomplete = !allDay && (!start || !end);
  const priorityInvalid = !/^\d+$/.test(priority.trim());
  const canSave =
    !saving && isValidPhone(phone) && !windowIncomplete && !priorityInvalid;

  const wraps =
    !allDay &&
    start !== "" &&
    end !== "" &&
    (timeInputToMinutes(end) ?? 0) <= (timeInputToMinutes(start) ?? 0);

  const handleSave = () => {
    onSave({
      label: label.trim() || null,
      phone: normalisePhone(phone),
      priority: Number(priority.trim()),
      isActive,
      // Seven days selected and none selected mean the same thing to the
      // backend; send the array either way and let it normalise.
      daysOfWeek: days,
      startMinute: allDay ? null : timeInputToMinutes(start),
      endMinute: allDay ? null : timeInputToMinutes(end),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {contact ? "Edit support number" : "Add support number"}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <TextField
            label="Label"
            size="small"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Recharge support"
            helperText="Shown above the number on the portal"
            sx={{ flex: "1 1 220px" }}
          />
          <TextField
            label="Phone number"
            size="small"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={phoneInvalid}
            helperText={phoneInvalid ? "6-15 digits required" : "Spaces, +, - and brackets are stripped"}
            sx={{ flex: "1 1 220px" }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap", mb: 2 }}>
          <TextField
            label="Priority"
            size="small"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            error={priorityInvalid}
            helperText={priorityInvalid ? "Whole number" : "Lower is shown first (1 = top)"}
            sx={{ width: 180 }}
          />
          <FormControlLabel
            sx={{ mt: 0.5 }}
            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
            label={
              <Box>
                <Typography variant="body2">Active</Typography>
                <Typography variant="caption" color="text.secondary">
                  Off hides it from the portal completely, at every hour
                </Typography>
              </Box>
            }
          />
        </Box>

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Availability
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          Days staffed — leave all unselected for every day. Times are IST.
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={days}
          onChange={(_e, next: number[]) => setDays(next)}
          sx={{ flexWrap: "wrap", mb: 2 }}
        >
          {DAYS.map((d) => (
            <ToggleButton key={d.value} value={d.value} sx={{ textTransform: "none", px: 1.5 }}>
              {d.short}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <FormControlLabel
          control={
            <Switch
              checked={allDay}
              onChange={(e) => {
                setAllDay(e.target.checked);
                if (e.target.checked) {
                  setStart("");
                  setEnd("");
                }
              }}
            />
          }
          label="Available all day (no time window)"
        />

        {!allDay && (
          <Box sx={{ display: "flex", gap: 2, mt: 2, flexWrap: "wrap" }}>
            <TextField
              label="Opens"
              type="time"
              size="small"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
            <TextField
              label="Closes"
              type="time"
              size="small"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 160 }}
            />
          </Box>
        )}

        {windowIncomplete && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
            Set both an opening and a closing time, or switch on "available all day".
          </Typography>
        )}

        {wraps && (
          // Worth saying out loud: an operator entering 18:00–09:00 means an
          // overnight line, and should be told we read it that way rather than
          // discovering it from the portal.
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "#B45309" }}>
            Closing time is at or before the opening time — this will be treated as an
            overnight window that runs into the next day.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={handleSave}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {saving ? "Saving…" : contact ? "Save changes" : "Add number"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportContactDialog;
